import { useState } from 'react';

import { ActivateButton } from './ActivateButton';
import { AUDIO_REPAIR_ID } from '../core/input/activationRegistry';
import { repairLabel } from '../core/speech/repairLabel';
import { useProfileStore } from '../core/profile/ProfileStore';
import { de } from '../i18n/de';
import { metaRepo } from '../data/repositories';
import { speechService } from '../core/speech/SpeechService';
import { useAudioStatus } from '../core/speech/audioStatusStore';
import { useComposerStore } from '../core/composer/useComposer';
import { ContentBand } from './ContentBand';

/**
 * `Ton aktivieren`, with the verb its mode can deliver (ADR-047), and the mute warning.
 *
 * Inside the content band, because status text is search-driven — the person has to notice it,
 * and under a field loss on an unknown side the edges of the screen may not exist for them
 * (§5.2, non-negotiable §9).
 *
 * The banner is a button, not a notice. When audio is dead the only thing that revives it is a
 * real gesture, so the thing telling you about the problem must also be the thing that fixes it.
 *
 * **And it now repairs rather than merely resuming (`SUR-23`, C80).** A tap used to call
 * `unlockFromGesture()`, which cannot revive a context iOS has closed — so on that path the sentence
 * `Ton aktivieren – antippen` was a promise the code could not keep, and the person tapped a button that
 * was structurally incapable of helping. It is the same three levels as the band's offer, in the same
 * words, because two controls meaning the same thing differently are two things to learn.
 *
 * This is also the repair control on the surfaces that have no band: `PredictionRow` is rendered by
 * Sprechen only, so the band's offer does not exist on Sätze or Notfall.
 */
export function AudioBanner(): React.JSX.Element | null {
  const needsUnlock = useAudioStatus((s) => s.needsUnlock);
  const silentPlayback = useAudioStatus((s) => s.silentPlayback);
  const repairFromGesture = useAudioStatus((s) => s.repairFromGesture);
  const [exhausted, setExhausted] = useState(false);
  // ADR-047: the verb has to be the one this person's mode can deliver.
  const inputMode = useProfileStore((s) => s.settings.inputMode);

  if (!needsUnlock && !silentPlayback) return null;

  const isUnlockProblem = needsUnlock;

  /**
   * The mute warning is deliberately **not** part of the escalation.
   *
   * A silent playback has a different remedy — check the volume and the Control Center mute — and
   * reloading the app would not touch it. Offering a repair for it would be the C64 mistake again: a
   * control that looks like a fix for something it cannot fix.
   *
   * It also names no gesture *on this control* — it asks the person to check a switch on the iPad — so it is
   * one string in every mode. The other two are the repair offer's own words (`SUR-23`, ADR-047).
   */
  const label = !isUnlockProblem
    ? de.audioSilent
    : repairLabel(exhausted ? 'reload' : 'repair', inputMode);

  const onPress = (): void => {
    if (!isUnlockProblem) return;

    if (exhausted) {
      // The one reload `INV-02` permits: a human asked for it. Never mid-sentence.
      if (speechService.isSpeaking()) return;
      void metaRepo
        .update({ draft: useComposerStore.getState().state.text })
        .catch((err: unknown) => console.error('[audio] could not save the draft', err))
        .finally(() => window.location.reload());
      return;
    }

    void repairFromGesture().then((ok) => {
      if (!ok) setExhausted(true);
    });
  };

  return (
    <ContentBand data-level={!isUnlockProblem ? 'mute' : exhausted ? 'reload' : 'repair'}>
      {/* A registered target for the same reason as the band's offer (ADR-046, `CON-01`): on Sätze and Notfall
          this *is* the repair control, so a scan that cannot reach it is a surface with no way out. */}
      <ActivateButton
        id={AUDIO_REPAIR_ID}
        testId="audio-banner"
        onActivate={onPress}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 'var(--min-target)',
          padding: '8px 16px',
          fontSize: 'var(--font-button)',
          fontWeight: 700,
          textAlign: 'center',
          // Colour is not the only signal: the border and the bold weight carry it too
          // (§5.2 — never encode state in colour alone).
          color: 'var(--danger-fg)',
          background: 'var(--danger)',
          border: '4px solid var(--fg)',
          borderRadius: 'var(--radius)',
        }}
      >
        {label}
      </ActivateButton>
    </ContentBand>
  );
}
